'use client';

import { useTranslations } from 'next-intl';
import { HStack, Box } from '@terra/ui-styled-system';
import { CancelCircleIcon, CheckmarkCircle02Icon } from '@terra/ui-icons';
import { MetaDefault } from '../../../../../components/typography';
import { Button } from '../../../../../components/styled/button';
import type { PortalObjectResult } from '../../../utils/portal-object-result';
import type { OnOpenChatObject } from '../../hitl-types';

type HitlActionOutcomeRowProps = {
  failureMessage?: string;
  createdResult?: PortalObjectResult | null;
  onOpenObject?: OnOpenChatObject;
};

export function HitlActionOutcomeRow({
  failureMessage,
  createdResult,
  onOpenObject,
}: HitlActionOutcomeRowProps) {
  const t = useTranslations(
    'designSystem.chat.hitlRequestConfirmationPromptCard',
  );

  if (failureMessage) {
    return (
      <HStack gap={1.5} alignItems="center" w="full" minWidth={0}>
        <Box color="fg.error" display="flex" alignItems="center" flexShrink={0}>
          <CancelCircleIcon size={14} />
        </Box>
        <MetaDefault color="fg.error">
          {t('actionFailedStatus', { message: failureMessage })}
        </MetaDefault>
      </HStack>
    );
  }

  if (!createdResult) {
    return null;
  }

  const handleOpen = onOpenObject
    ? () => {
        onOpenObject({
          type: createdResult.objectType,
          id: createdResult.objectId,
          label: createdResult.title ?? createdResult.objectId,
        });
      }
    : undefined;

  return (
    <HStack gap={1.5} alignItems="center" w="full" minWidth={0}>
      <Box
        color="fg.neutral.success"
        display="flex"
        alignItems="center"
        flexShrink={0}
      >
        <CheckmarkCircle02Icon size={14} />
      </Box>
      <MetaDefault color="fg.muted">{t('actionCreatedStatus')}</MetaDefault>
      {handleOpen ? (
        <Button variant="link" size="xs" onClick={handleOpen}>
          {t('openInPortal')}
        </Button>
      ) : null}
    </HStack>
  );
}
